import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import { FiRefreshCw, FiAlertCircle, FiAlertTriangle, FiZap, FiClock, FiCheckCircle } from 'react-icons/fi';
import { getVolumeReport, getValidationErrors, getApprovalSla } from '../../utils/journalApi';

const DOC_COLORS  = ['#5b5fc7','#10b981','#f59e0b','#ef4444','#0891b2','#a855f7','#ec4899','#84cc16'];
const TIER_COLORS = ['#6366f1','#f59e0b','#ef4444','#8b5cf6'];
const MONTH_NAMES = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const SEV = {
  ERROR:   { bg:'#fee2e2', fg:'#b91c1c', bar:'#ef4444', icon: <FiAlertCircle size={13}/> },
  WARNING: { bg:'#fef3c7', fg:'#92400e', bar:'#f59e0b', icon: <FiAlertTriangle size={13}/> },
};

const STATUS_DOT = {
  APPROVED: '#22c55e', PENDING: '#f59e0b', REJECTED: '#ef4444', AMENDED: '#f97316',
};

/* ── Custom tooltip for volume chart ── */
const VolumeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{ background:'#fff', border:'1px solid #d8e3f0', borderRadius:10, padding:'10px 14px', fontSize:12, boxShadow:'0 8px 24px rgba(13,38,74,0.12)' }}>
      <div style={{ fontWeight:800, marginBottom:6, color:'#12213a' }}>{label} <span style={{ color:'#57708d', fontWeight:600 }}>— {total} total</span></div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display:'flex', alignItems:'center', gap:7, marginBottom:3 }}>
          <div style={{ width:8, height:8, borderRadius:2, background:p.fill }} />
          <span style={{ color:'#57708d' }}>{p.dataKey}</span>
          <span style={{ fontWeight:700, color:'#12213a', marginLeft:'auto', paddingLeft:12 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function JournalReports() {
  const [volume,  setVolume]  = useState([]);
  const [valErrs, setValErrs] = useState([]);
  const [sla,     setSla]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [volF,    setVolF]    = useState({ year: new Date().getFullYear().toString(), company_code:'' });
  const [errF,    setErrF]    = useState({ days:'30', company_code:'' });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [v, e, s] = await Promise.all([
        getVolumeReport({ year:volF.year, company_code:volF.company_code||undefined }),
        getValidationErrors(errF.days, errF.company_code||undefined),
        getApprovalSla(),
      ]);
      const raw = Array.isArray(v.data?.monthly_breakdown) ? v.data.monthly_breakdown : [];
      const pivotMap = {};
      raw.forEach(({ month, document_type, count }) => {
        const key = Number(month);
        if (!pivotMap[key]) pivotMap[key] = { month: MONTH_NAMES[key] || String(key), _order: key };
        pivotMap[key][document_type] = count;
      });
      setVolume(Object.values(pivotMap).sort((a, b) => a._order - b._order));
      setValErrs(Array.isArray(e.data?.errors) ? e.data.errors : []);
      setSla(Array.isArray(s.data?.tiers) ? s.data.tiers : []);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const docTypes  = volume.length
    ? [...new Set(volume.flatMap(m => Object.keys(m).filter(k => k !== 'month' && k !== '_order')))]
    : [];
  const maxErr    = valErrs.length ? Math.max(...valErrs.map(e => e.count)) : 1;
  const totalVol  = volume.reduce((s, m) => s + docTypes.reduce((ss, dt) => ss + (m[dt] || 0), 0), 0);

  /* group SLA rows by tier for the visual cards */
  const slaByTier = sla.reduce((acc, r) => {
    if (!acc[r.tier]) acc[r.tier] = [];
    acc[r.tier].push(r);
    return acc;
  }, {});

  return (
    <div>
      {/* ══ Volume Chart ══ */}
      <div className="journal-panel">
        <div className="journal-panel-head" style={{ flexWrap:'wrap', gap:12 }}>
          <div>
            <h3 style={{ marginBottom:2 }}>Monthly Posting Volume</h3>
            {totalVol > 0 && (
              <span style={{ fontSize:'0.78rem', color:'var(--j-muted)' }}>
                {totalVol.toLocaleString()} entries in {volF.year}
              </span>
            )}
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <input className="journal-input" placeholder="Year" maxLength={4} value={volF.year}
              onChange={e => setVolF(f => ({...f, year:e.target.value}))} style={{ width:74 }} />
            <input className="journal-input" placeholder="Co. Code" maxLength={4} value={volF.company_code}
              onChange={e => setVolF(f => ({...f, company_code:e.target.value}))} style={{ width:100 }} />
            <button className="journal-btn primary small" onClick={loadAll} disabled={loading}>
              <FiRefreshCw size={12} className={loading?'spin':''} /> Apply
            </button>
          </div>
        </div>

        {/* Doc type summary pills */}
        {docTypes.length > 0 && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
            {docTypes.map((dt, i) => {
              const total = volume.reduce((s, m) => s + (m[dt] || 0), 0);
              return (
                <div key={dt} style={{
                  display:'inline-flex', alignItems:'center', gap:6,
                  background:'rgba(91,95,199,0.07)', border:'1px solid rgba(91,95,199,0.15)',
                  borderRadius:20, padding:'4px 11px',
                }}>
                  <div style={{ width:8, height:8, borderRadius:2, background:DOC_COLORS[i%DOC_COLORS.length] }} />
                  <span style={{ fontSize:11, fontWeight:700, color:'#12213a' }}>{dt}</span>
                  <span style={{ fontSize:11, color:'#57708d' }}>{total}</span>
                </div>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="journal-loading"><div className="journal-spinner" /><span>Loading…</span></div>
        ) : volume.length ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={volume} margin={{ top:4, bottom:4, left:-12, right:4 }} barSize={32}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,76,129,0.07)" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize:11, fill:'#57708d', fontWeight:600 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:10, fill:'#57708d' }} axisLine={false} tickLine={false} />
              <Tooltip content={<VolumeTooltip />} />
              <Legend
                wrapperStyle={{ fontSize:11, paddingTop:10 }}
                formatter={v => <span style={{ color:'#57708d', fontWeight:600 }}>{v}</span>}
              />
              {docTypes.map((dt, i) => (
                <Bar key={dt} dataKey={dt} name={dt} stackId="a"
                  fill={DOC_COLORS[i % DOC_COLORS.length]}
                  radius={i === docTypes.length - 1 ? [6,6,0,0] : [0,0,0,0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="journal-empty" style={{ padding:'40px 0' }}>
            No volume data for {volF.year}
          </div>
        )}
      </div>

      {/* ══ Bottom row ══ */}
      <div className="journal-grid-2">

        {/* ── Validation Errors ── */}
        <div className="journal-panel">
          <div className="journal-panel-head" style={{ flexWrap:'wrap', gap:8 }}>
            <h3>Validation Errors</h3>
            <div style={{ display:'flex', gap:7, alignItems:'center', flexWrap:'wrap' }}>
              <select className="journal-input" value={errF.days}
                onChange={e => setErrF(f => ({...f, days:e.target.value}))} style={{ width:128 }}>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
              <input className="journal-input" placeholder="Co." maxLength={4} value={errF.company_code}
                onChange={e => setErrF(f => ({...f, company_code:e.target.value}))} style={{ width:72 }} />
              <button className="journal-btn ghost small" onClick={loadAll}>Apply</button>
            </div>
          </div>

          {loading ? (
            <div className="journal-loading"><div className="journal-spinner" /><span>Loading…</span></div>
          ) : valErrs.length ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {valErrs.map((e, i) => {
                const s   = SEV[e.severity] || SEV.WARNING;
                const pct = Math.max(8, (e.count / maxErr) * 100);
                return (
                  <motion.div key={i}
                    initial={{ opacity:0, x:-12 }}
                    animate={{ opacity:1, x:0 }}
                    transition={{ delay: i * 0.06 }}
                    style={{
                      background: s.bg,
                      border: `1px solid ${s.bar}30`,
                      borderLeft: `4px solid ${s.bar}`,
                      borderRadius: 10,
                      padding: '10px 14px',
                    }}
                  >
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <span style={{ color: s.fg }}>{s.icon}</span>
                        <code style={{ fontSize:12, fontFamily:'monospace', fontWeight:800, color:'#12213a' }}>
                          {e.type}
                        </code>
                        <span style={{
                          fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:20,
                          background:`${s.bar}25`, color: s.fg,
                        }}>{e.severity}</span>
                      </div>
                      <span style={{ fontSize:18, fontWeight:800, color: s.fg }}>{e.count}</span>
                    </div>
                    <div style={{ height:5, background:`${s.bar}25`, borderRadius:4, overflow:'hidden' }}>
                      <motion.div
                        initial={{ width:0 }}
                        animate={{ width:`${pct}%` }}
                        transition={{ duration:0.7, delay: i * 0.08 }}
                        style={{ height:'100%', background: s.bar, borderRadius:4 }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="journal-empty">
              <FiZap size={32} style={{ color:'#22c55e' }} />
              No errors in this period
            </div>
          )}
        </div>

        {/* ── SLA Compliance ── */}
        <div className="journal-panel">
          <div className="journal-panel-head">
            <h3>Approval SLA Compliance</h3>
            <span style={{ fontSize:'0.78rem', color:'var(--j-muted)' }}>By tier &amp; status</span>
          </div>

          {loading ? (
            <div className="journal-loading"><div className="journal-spinner" /><span>Loading…</span></div>
          ) : Object.keys(slaByTier).length ? (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {Object.entries(slaByTier).map(([tier, rows]) => {
                const color  = TIER_COLORS[(Number(tier) - 1) % TIER_COLORS.length];
                const total  = rows.reduce((s, r) => s + r.count, 0);
                const approved = rows.find(r => r.status === 'APPROVED');
                const pending  = rows.find(r => r.status === 'PENDING');
                const avgMin   = approved?.avg_decision_minutes ?? 0;
                const approvedPct = total > 0 && approved ? Math.round((approved.count / total) * 100) : 0;

                return (
                  <div key={tier} style={{
                    border: `1px solid ${color}28`,
                    borderRadius: 12,
                    overflow: 'hidden',
                  }}>
                    {/* Tier header */}
                    <div style={{
                      background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
                      borderBottom: `1px solid ${color}20`,
                      padding: '10px 14px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                        <div style={{
                          width:32, height:32, borderRadius:9,
                          background: `${color}22`,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          border: `1.5px solid ${color}40`,
                        }}>
                          <span style={{ fontSize:13, fontWeight:800, color }}>{tier}</span>
                        </div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:'#12213a' }}>Tier {tier}</div>
                          <div style={{ fontSize:10, color:'#57708d' }}>{total} total decisions</div>
                        </div>
                      </div>
                      {avgMin > 0 && (
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:16, fontWeight:800, color }}>{avgMin} min</div>
                          <div style={{ fontSize:10, color:'#57708d' }}>avg decision</div>
                        </div>
                      )}
                    </div>

                    {/* Status rows */}
                    <div style={{ padding:'10px 14px', display:'flex', flexDirection:'column', gap:7 }}>
                      {rows.map((r, ri) => {
                        const dotColor = STATUS_DOT[r.status] || '#9ca3af';
                        const rowPct   = total > 0 ? (r.count / total) * 100 : 0;
                        return (
                          <div key={ri}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:3 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                                <div style={{ width:8, height:8, borderRadius:50, background:dotColor }} />
                                <span style={{ fontSize:11, fontWeight:700, color:'#12213a' }}>{r.status}</span>
                              </div>
                              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                <span style={{ fontSize:11, color:'#57708d' }}>{r.count} decisions</span>
                                <span style={{ fontSize:11, fontWeight:800, color:dotColor }}>{rowPct.toFixed(0)}%</span>
                              </div>
                            </div>
                            <div style={{ height:4, background:'rgba(0,0,0,0.06)', borderRadius:3, overflow:'hidden' }}>
                              <motion.div
                                initial={{ width:0 }}
                                animate={{ width:`${rowPct}%` }}
                                transition={{ duration:0.8, delay: ri * 0.1 }}
                                style={{ height:'100%', background:dotColor, borderRadius:3 }}
                              />
                            </div>
                          </div>
                        );
                      })}

                      {/* Approval rate indicator */}
                      {approvedPct > 0 && (
                        <div style={{
                          marginTop:4, display:'flex', alignItems:'center', gap:6,
                          background: approvedPct >= 80 ? '#f0fdf4' : '#fffbeb',
                          border: `1px solid ${approvedPct >= 80 ? '#bbf7d0' : '#fde68a'}`,
                          borderRadius:7, padding:'5px 10px',
                        }}>
                          {approvedPct >= 80
                            ? <FiCheckCircle size={12} style={{ color:'#15803d' }} />
                            : <FiClock size={12} style={{ color:'#d97706' }} />
                          }
                          <span style={{ fontSize:11, fontWeight:700, color: approvedPct >= 80 ? '#15803d' : '#92400e' }}>
                            {approvedPct}% approval rate
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="journal-empty">No SLA data</div>
          )}
        </div>
      </div>
    </div>
  );
}
